import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Eye, Calendar as CalendarIcon, Filter, X } from "lucide-react";
import { api } from "../../lib/api";
import { TornEdge } from "../../components/sport/TornEdge";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

const CATS = [
  { k: "all", label: "Toutes" },
  { k: "matchs", label: "Matchs & Résumés" },
  { k: "interviews", label: "Interviews" },
  { k: "coulisses", label: "Coulisses" },
  { k: "highlights", label: "Highlights" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function SportMedias() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [bgVideoId, setBgVideoId] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null); // { id, title }

  useEffect(() => {
    api.get("/sport/videos?limit=50")
      .then((r) => {
        const list = r.data?.videos || [];
        setVideos(list);
        if (list.length) setBgVideoId(list[0].id);
      })
      .finally(() => setLoading(false));
  }, []);

  // ESC ferme la modal lecteur
  useEffect(() => {
    if (!playingVideo) return;
    const onKey = (e) => { if (e.key === "Escape") setPlayingVideo(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [playingVideo]);

  const filtered = useMemo(() => {
    if (active === "all") return videos;
    return videos.filter((v) => v.category === active);
  }, [videos, active]);

  return (
    <div style={{ background: BG_DARK }}>
      {/* HERO with video background */}
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-16 overflow-hidden min-h-[60vh] flex items-center">
        {bgVideoId && (
          <iframe
            title="Background video"
            src={`https://www.youtube.com/embed/${bgVideoId}?autoplay=1&mute=1&loop=1&playlist=${bgVideoId}&controls=0&showinfo=0&modestbranding=1&playsinline=1`}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full pointer-events-none"
            allow="autoplay; encrypted-media"
            frameBorder="0"
            loading="lazy"
          />
        )}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(180deg, rgba(10,4,16,0.7) 0%, rgba(10,4,16,0.95) 100%), radial-gradient(ellipse at center, transparent 0%, rgba(10,4,16,0.6) 100%)`,
        }}/>
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 text-center w-full">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">
            Médiathèque officielle
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 0.95 }}>
            Toutes nos <span style={{ color: SECONDARY }}>vidéos</span>
          </motion.h1>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto">Synchronisé en direct avec notre chaîne YouTube — résumés, interviews, coulisses…</p>
        </div>
        <TornEdge position="bottom" color={BG_DEEP} variant="rip" height={48}/>
      </section>

      {/* CATEGORIES */}
      <section className="py-12" style={{ background: BG_DEEP }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            <Filter size={14} className="text-pink-400 shrink-0"/>
            {CATS.map((c) => {
              const cnt = c.k === "all" ? videos.length : videos.filter((v) => v.category === c.k).length;
              return (
                <button data-testid={`cat-${c.k}`}
                  key={c.k}
                  onClick={() => setActive(c.k)}
                  className={`shrink-0 px-4 py-2 rounded-full font-black uppercase text-[11px] tracking-widest transition ${active === c.k ? "bg-pink-500 text-black shadow-lg" : "bg-white/5 text-white border border-white/10 hover:bg-white/10"}`}>
                  {c.label} <span className="opacity-60 ml-1">({cnt})</span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="text-white/50 italic text-center py-20">Chargement des vidéos…</div>
          ) : filtered.length === 0 ? (
            <div className="text-white/50 italic text-center py-20">Aucune vidéo dans cette catégorie pour le moment.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((v, i) => (
                <motion.button
                  key={v.id} type="button"
                  onClick={() => setPlayingVideo({ id: v.id, title: v.title })}
                  data-testid={`video-${v.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                  className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-pink-500/50 hover:bg-white/10 transition flex flex-col text-left"
                >
                  <div className="relative aspect-video">
                    <img src={v.thumbnail_hd} alt={v.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500" onError={(e) => { e.currentTarget.src = v.thumbnail; }}/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-pink-500/95 text-black flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
                        <Play size={20} fill="black" className="ml-0.5"/>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 inline-block px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest bg-black/70 text-pink-300">{v.category}</div>
                  </div>
                  <div className="p-4 flex-1">
                    <h3 className="text-white text-sm font-bold leading-tight line-clamp-2 group-hover:text-pink-300 transition">{v.title}</h3>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-white/50">
                      <span className="inline-flex items-center gap-1"><CalendarIcon size={10}/> {fmtDate(v.published_at)}</span>
                      {v.view_count ? <span className="inline-flex items-center gap-1"><Eye size={10}/> {v.view_count.toLocaleString("fr-FR")}</span> : null}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lecteur vidéo modal (YouTube embed) */}
      <AnimatePresence>
        {playingVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="video-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
            style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }}
            onClick={() => setPlayingVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              transition={{ type: "spring", damping: 22, stiffness: 220 }}
              className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-[0_25px_80px_rgba(236,31,124,0.35)] bg-black"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                key={playingVideo.id}
                src={`https://www.youtube.com/embed/${playingVideo.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                title={playingVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
              />
              <button
                type="button"
                onClick={() => setPlayingVideo(null)}
                data-testid="video-modal-close"
                aria-label="Fermer la vidéo"
                className="absolute -top-12 right-0 sm:top-3 sm:right-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/70 hover:bg-pink-600 text-white text-[11px] font-black uppercase tracking-widest transition border border-white/15 z-10"
              >
                <X size={14}/> Fermer
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
